import {Power3, TimelineMax} from "gsap";
import {AssetLoader} from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Helper} from "@nolimitcity/slot-game/bin/utils/Helper";
import * as PIXI from "pixi.js";

export interface SpinePlayConfig {
    name: string;
    track?: number;
    duration?: number;
    delay?: number;
    animations?: string[];
    skinType?: string;
    loop?: boolean;
    showOnEnd?: boolean;
    onStart?: Function;
    onComplete?: Function;
}

export class SpinePlayer extends PIXI.Container {

    private static loader: AssetLoader;
    private _tl: TimelineMax;
    private _tlSwitch: TimelineMax;
    private _spineSwitch: PIXI.spine.Spine | undefined;
    private _anims: string[];

    private _path: string;

    constructor(spinePath: string, initVisible: boolean = false) {
        super();
        this.changeSpine(spinePath);
        this.visible = initVisible;
    }

    private _spine: PIXI.spine.Spine | undefined;

    public get spine(): PIXI.spine.Spine {
        return this._spine!;
    }

    public get animations(): string[] {
        return this._anims;
    }

    public get timeLine(): TimelineMax {
        return this._tl;
    }

    public get isPlaying(): boolean {
        return !!this._tl && this._tl.isActive();
    }

    public static setSpineLoader(loader: AssetLoader): void {
        this.loader = loader
    }

    public static fetchDuration(_spinePath: string, animationName: string): number {
        const durr: number | undefined = SpinePlayer.loader.getSpineAsset(_spinePath).animations.find(anim => {
            return anim.name === animationName
        })?.duration;

        if (durr == undefined) Logger.warn("Couldn't find animation: " + animationName + " in Spine" + _spinePath);

        return durr == undefined ? 0 : durr;
    }

    public animateSpineSwitch(toPath: string, conf: SpinePlayConfig, fadeDurr: number): TimelineMax {
        this._tlSwitch = Helper.setTimelineMax(this._tlSwitch, new TimelineMax({
            onComplete: () => this.removeSpineSwitch()
        }));

        this._spineSwitch = this._spine;

        this._path = toPath;

        this._tlSwitch.add(this.play(conf), 0);

        this._tlSwitch.fromTo(this._spine!, {alpha: 0}, {alpha: 1, duration: fadeDurr, ease: Power3.easeInOut}, 0);
        this._tlSwitch.fromTo(this._spineSwitch!, {alpha: 1}, {
            alpha: 0,
            duration: fadeDurr,
            ease: Power3.easeInOut
        }, 0);

        return this._tlSwitch;
    }

    public changeSpine(path: string): void {
        if (this._path == path) return;

        this._path = path;
        this.reset();

        this._anims = [];
        this._spine!.spineData.animations.forEach((d) => this._anims.push(d.name));
    }

    public play(conf?: SpinePlayConfig): TimelineMax {
        if (!conf) conf = {name: this._path};
        this.reset();
        this._tl = Helper.setTimelineMax(this._tl, new TimelineMax({
            onStart: () => {
                this.visible = true;
                conf?.onStart && conf.onStart();
            },
            onComplete: () => {
                this.visible = !!conf?.showOnEnd;
                conf?.onComplete && conf.onComplete();
            },
            repeat: !!conf.loop ? -1 : 0
        }));

        if (conf.skinType) this.spine.skeleton.setSkinByName(conf.skinType);
        this.spine.state.addAnimation(conf.track === undefined ? 0 : conf.track, conf.name, !!conf.loop, conf.delay === undefined ? 0 : conf.delay);
        const durr: number = conf.duration === undefined ? this.fetchDuration(conf.name) : conf.duration;
        this.spine.state.timeScale = this.fetchDuration(conf.name) / durr;
        this._tl.to(this, {duration: durr});
        return this._tl;
    }

    public playAllAnimationsInSequence(conf?: SpinePlayConfig): TimelineMax {
        if (!conf) conf = {name: this._path};

        this.reset();
        this._tl = Helper.setTimelineMax(this._tl, new TimelineMax({
            onStart: () => {
                this.visible = true;
                conf?.onStart && conf.onStart();
            },
            onComplete: () => {
                this.visible = !!conf?.showOnEnd;
                conf?.onComplete && conf.onComplete();
            },
            repeat: !!conf.loop ? -1 : 0
        }));

        if (conf.skinType) this.spine.skeleton.setSkinByName(conf.skinType);
        const durr: number = conf.duration === undefined ? this.fetchDuration(conf.name) : conf.duration;

        this.spine.spineData.animations.forEach((animation: PIXI.spine.core.Animation) => {
            this._tl.add(() => {
                this.spine.state.addAnimation(0, animation.name, false, 0);
                this.spine.state.timeScale = this.fetchDuration(animation.name) / durr;
            });
            this._tl.to(this, {duration: durr});
        });

        return this._tl;
    }

    public stop(playToEnd?: boolean): void {
        this._tl = Helper.setTimelineMax(this._tl, undefined, playToEnd)!;
        this.visible = false;
        this.reset();
    }
    public stopAndShowEnd(playToEnd?: boolean): void {
        this._tl = Helper.setTimelineMax(this._tl, undefined, playToEnd)!;
        this.reset();
        this.visible = true;
    }

    public fadeOut(playToEnd?: boolean): void {
        this._tl = Helper.setTimelineMax(this._tl, undefined, playToEnd)!;
        this._tl.to(this, 0.2, {
            alpha: 0, onComplete: () => {
                this.reset();
                this.visible = false;
                this.alpha = 1;
            }
        });
    }

    public fetchDuration(animationName: string): number {
        return SpinePlayer.fetchDuration(this._path, animationName);
    }

    public reset(): void {
        if (this.spine) {
            this.spine.destroy({children: true});
            this.removeChildren();
            delete this._spine;
        }
        this._spine = this.createSpine();
    }

    public onResize?(): void;

    protected createSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = this.addChild(new PIXI.spine.Spine(SpinePlayer.loader.getSpineAsset(this._path)));
        return spine;
    }

    private removeSpineSwitch(): void {
        if (this._spineSwitch) {
            this._spineSwitch.destroy({children: true});
            this.removeChildren();
            delete this._spineSwitch;
        }
    }
}
